import type { PublishedDraw } from "../../lotto-results";

export type TicketSource = "QR" | "MANUAL" | "GENERATED";
export type TicketGrade = "PENDING" | "RANK_1" | "RANK_2" | "RANK_3" | "RANK_4" | "RANK_5" | "NO_WIN";

export interface TicketImportResult { readonly drawNo: number | null; readonly games: readonly (readonly number[])[] }
export interface TicketMatch { readonly matchedMainNumbers: ReadonlySet<number>; readonly bonusMatched: boolean }
export interface TicketGameEvaluation extends TicketMatch { readonly grade: TicketGrade; readonly publishedPrizeAmount: number | null }

const ALLOWED_QR_HOSTS = new Set(["m.dhlottery.co.kr", "www.dhlottery.co.kr", "dhlottery.co.kr", "qr.dhlottery.co.kr"]);

export function normalizeTicketGame(numbers: readonly number[]): readonly number[] {
  if (numbers.length !== 6) throw new Error("게임 번호는 6개여야 합니다.");
  if (new Set(numbers).size !== 6) throw new Error("게임 번호는 중복될 수 없습니다.");
  if (numbers.some((number) => !Number.isInteger(number) || number < 1 || number > 45)) throw new Error("게임 번호는 1~45 범위여야 합니다.");
  return [...numbers].sort((left, right) => left - right);
}

/** Parses approved official/test payload forms locally. Raw QR text is never persisted. */
export function parseLottoQr(raw: string): TicketImportResult {
  const value = raw.trim();
  if (value.length > 2_048) throw new Error("QR 내용이 너무 깁니다.");
  if (value.startsWith("LOTTO|")) return parseDelimited(value);
  const compact = value.replace(/[^0-9mq]/g, "");
  if (!value.includes("://") && !value.includes("/") && !value.includes("?") && compact.length >= 16 && /^\d{4}/.test(compact) && /[mq]/.test(compact.slice(4))) return parseOfficialPayload(compact);
  let url: URL;
  try { url = new URL(value); } catch { throw new Error("지원하지 않는 복권 QR 형식입니다."); }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.port || url.username || url.password || !ALLOWED_QR_HOSTS.has(url.hostname.toLowerCase())) throw new Error("지원하지 않는 복권 QR 형식입니다.");
  const games = url.searchParams.get("games") ?? url.searchParams.get("numbers");
  if (games?.trim()) return { drawNo: positiveIntOrNull(url.searchParams.get("draw")), games: parseTicketGames(games) };
  const officialPayload = url.searchParams.get("v") ?? url.searchParams.get("V");
  if (!officialPayload?.trim()) throw new Error("번호를 확인할 수 없는 QR입니다.");
  return parseOfficialPayload(officialPayload);
}

export function parseTicketGames(raw: string): readonly (readonly number[])[] {
  const games = raw.split(";").filter((value) => value.trim()).map((game) => normalizeTicketGame(game.split(",").map((value) => Number(value.trim()))));
  if (!games.length || games.length > 5) throw new Error("복권은 1~5게임만 지원합니다.");
  return games;
}

export function gradeTicketGame(numbers: readonly number[], draw: PublishedDraw): TicketGameEvaluation {
  const normalized = normalizeTicketGame(numbers);
  const winningNumbers = new Set(draw.numbers);
  const matchedMainNumbers = new Set(normalized.filter((number) => winningNumbers.has(number)));
  const bonusMatched = normalized.includes(draw.bonusNumber);
  const main = matchedMainNumbers.size;
  const grade: TicketGrade = main === 6 ? "RANK_1"
    : main === 5 && bonusMatched ? "RANK_2"
      : main === 5 ? "RANK_3"
        : main === 4 ? "RANK_4"
          : main === 3 ? "RANK_5" : "NO_WIN";
  const rank = gradeRank(grade);
  return { grade, matchedMainNumbers, bonusMatched, publishedPrizeAmount: rank === null ? null : draw.prizes.find((prize) => prize.rank === rank)?.perWinnerAmount ?? null };
}

export function ticketGradeLabel(grade: TicketGrade): string {
  const labels: Record<TicketGrade, string> = { PENDING: "발표 대기", RANK_1: "1등", RANK_2: "2등", RANK_3: "3등", RANK_4: "4등", RANK_5: "5등", NO_WIN: "낙첨" };
  return labels[grade];
}

function parseDelimited(value: string): TicketImportResult {
  const fields = new Map(value.slice("LOTTO|".length).split("|").map((field) => {
    const separator = field.indexOf("=");
    return [separator < 0 ? field : field.slice(0, separator), separator < 0 ? "" : field.slice(separator + 1)] as const;
  }));
  const games = fields.get("games") ?? "";
  if (!games.trim()) throw new Error("번호를 확인할 수 없는 QR입니다.");
  return { drawNo: positiveIntOrNull(fields.get("draw") ?? null), games: parseTicketGames(games) };
}

function parseOfficialPayload(raw: string): TicketImportResult {
  const compact = raw.split(".", 1)[0]!.replace(/[^0-9mq]/g, "");
  if (compact.length < 4) throw new Error("회차를 확인할 수 없는 복권 QR입니다.");
  const drawNo = positiveIntOrNull(compact.slice(0, 4));
  if (drawNo === null) throw new Error("회차를 확인할 수 없는 복권 QR입니다.");
  const games = [...new Set(compact.slice(4).split(/[mq]/).filter(Boolean).flatMap((group) => {
    const digits = (group.match(/^\d+/)?.[0] ?? "");
    if (digits.length < 12) return [];
    try { return [normalizeTicketGame(Array.from({ length: 6 }, (_, index) => Number(digits.slice(index * 2, index * 2 + 2))))]; } catch { return []; }
  }).map((game) => game.join(",")))].slice(0, 5).map((game) => game.split(",").map(Number));
  if (!games.length) throw new Error("번호를 확인할 수 없는 복권 QR입니다.");
  return { drawNo, games };
}

function gradeRank(grade: TicketGrade): number | null {
  return grade === "RANK_1" ? 1 : grade === "RANK_2" ? 2 : grade === "RANK_3" ? 3 : grade === "RANK_4" ? 4 : grade === "RANK_5" ? 5 : null;
}

function positiveIntOrNull(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
